import * as React from "react";
import { Upload, X } from "../icons";

import { Button } from "../button";
import { Card, CardContent } from "../card";
import { cn } from "../utils";

export interface FileInputProps {
  /** Currently selected file (controlled). */
  value?: File | null;
  /** Called with the new file or null when cleared. */
  onChange?: (file: File | null) => void;
  /** Accept attribute for native file picker. */
  accept?: string;
  /** Max bytes — rejected with `onError` if exceeded. */
  maxBytes?: number;
  /** Called when validation fails (e.g. too big, wrong type). */
  onError?: (message: string) => void;
  /** Disabled state. */
  disabled?: boolean;
  /** Optional help/hint shown below the button when no file is selected. */
  hint?: React.ReactNode;
  /** Allow multiple files — passed to the underlying input. */
  multiple?: boolean;
  className?: string;
  /** Optional id (for label htmlFor linkage). */
  id?: string;
  /** Enable drag-and-drop onto the card (§2.8.3 — DropZone reuses this seam). A
   *  file dropped on the card goes through the same validation as the picker. */
  droppable?: boolean;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Card-wrapped file picker. Hides the native file input and surfaces a
 * primary <Button> trigger. After selection shows filename + size with a
 * clear button. Validates against `maxBytes`.
 */
export function FileInput({
  value,
  onChange,
  accept,
  maxBytes,
  onError,
  disabled,
  hint,
  multiple,
  className,
  id,
  droppable,
}: FileInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const pick = React.useCallback(() => {
    inputRef.current?.click();
  }, []);

  const acceptFile = React.useCallback(
    (file: File | null) => {
      if (file && maxBytes && file.size > maxBytes) {
        onError?.(
          `File is ${formatBytes(file.size)} — max ${formatBytes(maxBytes)}.`,
        );
        // Clear the picker so the same file can be re-selected later.
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
      onChange?.(file);
    },
    [maxBytes, onChange, onError],
  );

  const onPickerChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      acceptFile(e.target.files?.[0] ?? null);
    },
    [acceptFile],
  );

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      if (!droppable || disabled) return;
      e.preventDefault();
      setDragOver(false);
      acceptFile(e.dataTransfer.files?.[0] ?? null);
    },
    [droppable, disabled, acceptFile],
  );

  const clear = React.useCallback(() => {
    if (inputRef.current) inputRef.current.value = "";
    onChange?.(null);
  }, [onChange]);

  return (
    <Card
      data-slot="file-input"
      // The global drop layer suppresses its viewport overlay over an explicit
      // intake drop target so the two don't fight for the same drop (§2.8.3 guard).
      data-intake-dropzone={droppable ? "" : undefined}
      className={cn(
        "py-4 transition-colors",
        droppable && dragOver && "border-primary bg-primary/5",
        className,
      )}
      onDragOver={
        droppable && !disabled
          ? (e) => {
              e.preventDefault();
              setDragOver(true);
            }
          : undefined
      }
      onDragLeave={droppable ? () => setDragOver(false) : undefined}
      onDrop={droppable ? onDrop : undefined}
    >
      <CardContent className="flex items-center gap-3 px-4">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={onPickerChange}
          className="sr-only"
        />
        {value ? (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate type-body text-foreground">
                {value.name}
              </span>
              <span className="type-subtext text-muted-foreground">
                {formatBytes(value.size)}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              disabled={disabled}
              aria-label="Clear file"
            >
              <X className="size-4" />
              Clear
            </Button>
          </>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="type-body text-foreground">No file selected</span>
              {hint && (
                <span className="type-subtext text-muted-foreground">{hint}</span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={pick}
              disabled={disabled}
            >
              <Upload className="size-4" />
              Choose file
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
